import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LoadingState, ErrorMessage } from "@/components/ui/States";

export default function StormToggle() {
    const [doc, setDoc] = useState(null);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get("/admin/storm-season-mode")
            .then((r) => {
                setDoc(r.data);
                setReason(r.data?.reason || "");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const toggle = async () => {
        if (!doc) return;
        const next = !doc.is_active;
        if (next && !reason.trim()) {
            setErr("Please add a reason before turning Storm Mode on (e.g., 'Hurricane watch — Collier County').");
            return;
        }
        setErr(null);
        setSubmitting(true);
        try {
            await api.patch("/admin/storm-season-mode", {
                is_active: next,
                reason: reason.trim(),
            });
            load();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to toggle storm mode.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <LoadingState />;
    if (!doc) return null;

    return (
        <div data-testid="storm-toggle-page">
            <h1 className="mb-2">Storm Season Mode</h1>
            <p className="text-sm text-[var(--color-text-muted)] mb-4">
                Manual toggle. When ON, the public site shifts CTA emphasis to storm damage and tags new leads as P1-storm.
                Storm repair availability may vary by schedule and season.
            </p>

            <div className={`a1-card mb-4 ${doc.is_active ? "border-[var(--color-storm)]" : ""}`} data-testid="storm-toggle-card">
                <p className="font-bold text-lg mb-2">
                    Status: {doc.is_active ? <span style={{ color: "var(--color-storm)" }}>ACTIVE</span> : "OFF"}
                </p>
                {doc.is_active ? (
                    <>
                        <p className="text-sm">Activated at: {doc.activated_at}</p>
                        <p className="text-sm">Activated by: {doc.activated_by}</p>
                    </>
                ) : null}
                <label className="a1-label mt-4">Reason / context</label>
                <input
                    className="a1-input"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g., Hurricane watch — Collier County"
                    data-testid="storm-reason-input"
                />
                {err ? <ErrorMessage message={err} testId="storm-toggle-error" /> : null}
                <button onClick={toggle} disabled={submitting} className={doc.is_active ? "a1-btn-secondary mt-3" : "a1-btn-primary mt-3"} data-testid="storm-toggle-btn">
                    {submitting ? "Saving…" : doc.is_active ? "Turn Storm Mode OFF" : "Turn Storm Mode ON"}
                </button>
            </div>

            <div className="a1-card">
                <h3 className="text-lg mb-2">Audit log</h3>
                {Array.isArray(doc.audit_log) && doc.audit_log.length ? (
                    <ul className="text-sm space-y-1">
                        {doc.audit_log.slice().reverse().map((a, i) => (
                            <li key={i}>
                                {a.at} — {a.actor} → {a.is_active ? "ON" : "OFF"} ({a.reason || "no reason"})
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-[var(--color-text-muted)]">No history yet.</p>
                )}
            </div>
        </div>
    );
}
